/**
 * The main-thread side of the decoding worker: it sends the frames over and takes the decoder's
 * statistics back. The generated track is made where the browser allows: Chrome generates on
 * this thread and hands the worker the generator's writable, Safari generates in the worker and
 * sends the track here.
 */

import type {
    DataChannelVideoTrack, DataChannelVideoWorkerReply, DataChannelVideoWorkerRequest,
    DeviceStreamInfo, EpochAudioClock,
} from '@/types/player';
import type { VideoMessage } from '@/types/webrtc';

export class WorkerVideoTrack implements DataChannelVideoTrack {
    /** Empty until the worker posts its track; <video> picks the track up when it is added. */
    readonly stream: MediaStream;

    private readonly worker: Worker;

    private info: DeviceStreamInfo | null = null;

    private isClosed = false;

    constructor() {
        this.worker = new Worker(
            new URL('./dataChannelVideo.worker.ts', import.meta.url), { type: 'module' });
        this.worker.onmessage = (event: MessageEvent<DataChannelVideoWorkerReply>): void => {
            this.onReply(event.data);
        };
        this.worker.onerror = (event: ErrorEvent): void => {
            console.warn('[WorkerVideoTrack] worker failed', event.message);
        };
        if (typeof MediaStreamTrackGenerator === 'function') {
            const generator = new MediaStreamTrackGenerator({ kind: 'video' });
            this.stream = new MediaStream([generator]);
            const request: DataChannelVideoWorkerRequest = {
                type: 'sink', writable: generator.writable,
            };
            this.worker.postMessage(request, [generator.writable]);
        } else {
            this.stream = new MediaStream();
        }
    }

    push(frame: VideoMessage): void {
        if (this.isClosed) { return; }
        const request: DataChannelVideoWorkerRequest = { type: 'frame', frame };
        const { buffer } = frame.data;
        this.worker.postMessage(request, buffer instanceof ArrayBuffer ? [buffer] : []);
    }

    getInfo(): DeviceStreamInfo | null {
        return this.info;
    }

    setAudioClock(clock: EpochAudioClock | null): void {
        if (this.isClosed) { return; }
        const request: DataChannelVideoWorkerRequest = { type: 'audioClock', clock };
        this.worker.postMessage(request);
    }

    close(): void {
        if (this.isClosed) { return; }
        this.isClosed = true;
        const request: DataChannelVideoWorkerRequest = { type: 'close' };
        this.worker.postMessage(request);
        this.worker.terminate();
        // The audio tracks are the camera's RTP ones, lent to this stream.
        for (const track of this.stream.getVideoTracks()) { track.stop(); }
    }

    private onReply(reply: DataChannelVideoWorkerReply): void {
        if (this.isClosed) { return; }
        switch (reply.type) {
            case 'track':
                this.stream.addTrack(reply.track);
                break;
            case 'info':
                this.info = reply.info;
                break;
            case 'unsupported':
                console.warn('[WorkerVideoTrack] this browser generates no video track');
                this.close();
                break;
            default:
                break;
        }
    }
}
